<template>
    <el-dialog title="报售后" :visible="visibleAfterSales" width="760px" top="6vh" @close="closeDialog"
        :close-on-click-modal="false" append-to-body custom-class="after-sale-dialog">
        <div class="as-body">
            <!-- 主订单基础信息（只读展示） -->
            <div class="as-card as-card--info">
                <div class="as-card__title">
                    <i class="el-icon-document"></i>
                    <span>主订单信息</span>
                </div>
                <el-row :gutter="0" class="as-info-grid">
                    <el-col :span="12" class="as-info-cell">
                        <span class="as-info-label">报单编号</span>
                        <span class="as-info-value">{{ declaration.number || '-' }}</span>
                    </el-col>
                    <el-col :span="12" class="as-info-cell">
                        <span class="as-info-label">业务线</span>
                        <span class="as-info-value">{{ businessLabel(declaration.type) }}</span>
                    </el-col>
                    <el-col :span="12" class="as-info-cell">
                        <span class="as-info-label">客户姓名</span>
                        <span class="as-info-value">{{ declaration.customName || '-' }}</span>
                    </el-col>
                    <el-col :span="12" class="as-info-cell">
                        <span class="as-info-label">客户电话</span>
                        <span class="as-info-value">{{ declaration.customMobile || '-' }}</span>
                    </el-col>
                    <el-col :span="12" class="as-info-cell">
                        <span class="as-info-label">订单状态</span>
                        <span class="as-info-value">
                            <el-tag size="mini" type="success">{{ orderStatusLabel(declaration.orderStatus) }}</el-tag>
                        </span>
                    </el-col>
                    <el-col :span="24" class="as-info-cell">
                        <span class="as-info-label">客户地址</span>
                        <span class="as-info-value">{{ declaration.customAddress || '-' }}</span>
                    </el-col>
                </el-row>
            </div>

            <!-- 售后申请信息 -->
            <div class="as-card">
                <div class="as-card__title">
                    <i class="el-icon-edit-outline"></i>
                    <span>售后申请信息</span>
                </div>
                <el-form :model="form" ref="afterSaleForm" label-width="88px" :rules="rules" class="as-form">
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="联系人" prop="contactsName">
                                <el-input v-model="form.contactsName" placeholder="默认取主订单客户姓名"></el-input>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="联系电话" prop="contactsMobile">
                                <el-input v-model="form.contactsMobile" placeholder="默认取主订单客户电话"></el-input>
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-form-item label="指派工程师" prop="engineerId">
                        <el-select v-model="form.engineerId" placeholder="不选则进入待指派" filterable remote clearable
                            :remote-method="handleRemote" style="width: 100%;">
                            <el-option v-for="item in users" :key="item.id" :label="item.realName"
                                :value="item.id"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="是否过保" prop="overinsuredFlag">
                        <el-radio-group v-model="form.overinsuredFlag">
                            <el-radio label="YES">过保</el-radio>
                            <el-radio label="NO">保内</el-radio>
                            <el-radio label="PENDING">待工程师确认</el-radio>
                        </el-radio-group>
                    </el-form-item>
                    <el-form-item label="故障现象" prop="faultPhenomenon">
                        <el-input type="textarea" :rows="3" v-model="form.faultPhenomenon"
                            placeholder="请输入故障现象描述"></el-input>
                    </el-form-item>
                    <el-form-item label="故障照片">
                        <ImageUpload action="#" :multiple="true" :limit="5" @uploaded="handleUploaded"
                            :uploaded-images="faultPhotos" />
                        <div class="as-tip">最多上传 5 张图片，单张不超过 20M</div>
                    </el-form-item>
                    <el-form-item label="故障视频">
                        <videoAndFileUpload action="#" :multiple="false" :limit="1" :max-size="100" accept="video/*"
                            :show-file-list="false" @uploaded="handleUploadVideo" :uploaded-images="faultVideos" />
                        <div class="as-tip">支持 1 个视频，大小不超过 100M</div>
                    </el-form-item>
                    <el-form-item label="备注">
                        <el-input type="textarea" :rows="2" v-model="form.remarks" placeholder="选填"></el-input>
                    </el-form-item>
                </el-form>
            </div>
        </div>
        <span slot="footer" class="dialog-footer">
            <el-button size="small" @click="closeDialog" icon="el-icon-close">取消</el-button>
            <el-button type="primary" size="small" :loading="submitting" @click="submit"
                icon="el-icon-upload">提交</el-button>
        </span>
    </el-dialog>
</template>

<script>
import { createFromDeclaration } from "@/api/salesCloud/afterSale";
import { BUSINESS_TYPE_DIC } from "@/const/salesCloud/afterSaleOption";
import { findUserList } from "@/api/work/projectService";
import ImageUpload from "@/components/imageUpload/ImageUpload";
import videoAndFileUpload from "@/components/videoAndFileUpload/videoAndFileUpload";

// 主订单（报单）订单状态展示映射
const ORDER_STATUS_LABEL = {
    WAIT_FOR_ASSIGN: "待指派",
    WAIT_AUDIT: "待审核",
    IN_SERVICE: "服务中",
    TO_BE_EVALUATED: "待评价",
    COMPLETE: "已完成",
    CANCELLED: "已作废"
};

export default {
    name: "WaterProofAfterSales",
    components: {
        ImageUpload,
        videoAndFileUpload
    },
    props: {
        visibleAfterSales: {
            type: Boolean,
            required: true
        },
        // 主订单（报单）行数据
        declaration: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            form: this.getInitialForm(),
            faultPhotos: [],
            faultVideos: [],
            users: [],
            submitting: false,
            remoteTimeout: null,
            rules: {
                faultPhenomenon: [
                    { required: true, message: "请输入故障现象", trigger: "blur" }
                ],
                contactsMobile: [
                    { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号", trigger: "blur" }
                ],
                overinsuredFlag: [
                    { required: true, message: "请选择是否过保", trigger: "change" }
                ]
            }
        };
    },
    watch: {
        visibleAfterSales(newVal) {
            if (newVal) {
                this.initForm();
            }
        }
    },
    methods: {
        getInitialForm() {
            return {
                contactsName: "",
                contactsMobile: "",
                engineerId: "",
                overinsuredFlag: "",
                faultPhenomenon: "",
                remarks: ""
            };
        },
        initForm() {
            this.form = this.getInitialForm();
            this.form.contactsName = this.declaration.customName || "";
            this.form.contactsMobile = this.declaration.customMobile || "";
            this.faultPhotos = [];
            this.faultVideos = [];
            this.users = [];
            this.$nextTick(() => {
                this.$refs.afterSaleForm && this.$refs.afterSaleForm.clearValidate();
            });
        },
        businessLabel(val) {
            const item = BUSINESS_TYPE_DIC.find(i => i.value === val);
            return item ? item.label : (val || "-");
        },
        orderStatusLabel(val) {
            return ORDER_STATUS_LABEL[val] || val || "-";
        },
        handleRemote(query) {
            if (this.remoteTimeout) clearTimeout(this.remoteTimeout);
            this.remoteTimeout = setTimeout(() => {
                // eslint-disable-next-line no-control-regex
                const isChineseChar = /[^\x00-\xff]/.test(query);
                const isValidMobile = /^\d{2,}$/.test(query);
                if (isChineseChar || isValidMobile) {
                    const params = { type: "JOINS_ENGINEER", mobile: query };
                    findUserList(params).then(res => {
                        if (res && res.code === "200") {
                            const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
                            this.users = data || [];
                        } else {
                            this.$message.error(res && res.message);
                        }
                    });
                }
            }, 200);
        },
        handleUploaded(urls) {
            this.faultPhotos = urls;
        },
        handleUploadVideo(urls) {
            this.faultVideos = urls;
        },
        submit() {
            if (!this.declaration.id) {
                this.$message.error("缺少主订单id");
                return;
            }
            this.$refs.afterSaleForm.validate(valid => {
                if (!valid) return;
                this.submitting = true;
                const payload = {
                    declarationId: this.declaration.id,
                    businessType: "WATERPROOF",
                    overinsuredFlag: this.form.overinsuredFlag,
                    faultPhenomenon: this.form.faultPhenomenon,
                    faultPhotos: this.faultPhotos.map(i => i.url).join(","),
                    videoUrl: this.faultVideos.map(i => i.url).join(","),
                    contactsName: this.form.contactsName,
                    contactsMobile: this.form.contactsMobile,
                    remarks: this.form.remarks
                };
                if (this.form.engineerId) {
                    payload.engineerId = this.form.engineerId;
                }
                createFromDeclaration(payload).then(res => {
                    if (res && res.code == "200") {
                        this.$message.success("报售后成功");
                        this.$emit("success", res.data);
                        this.closeDialog();
                    } else {
                        this.$message.error((res && res.message) || "报售后失败");
                    }
                    this.submitting = false;
                }).catch(err => {
                    this.$message.error((err && err.message) || "报售后失败");
                    this.submitting = false;
                });
            });
        },
        closeDialog() {
            this.$emit("update:visibleAfterSales", false);
        }
    }
};
</script>

<style scoped>
.as-body {
    max-height: 68vh;
    overflow-y: auto;
    padding: 0 4px;
}

.as-card {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 16px 18px;
    margin-bottom: 16px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.as-card:last-child {
    margin-bottom: 0;
}

.as-card--info {
    background: #f4f8ff;
    border-color: #d9ecff;
}

.as-card__title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 14px;
    color: #303133;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #e4e7ed;
}

.as-card--info .as-card__title {
    border-bottom-color: #d9ecff;
}

.as-card__title i {
    color: #409eff;
    font-size: 16px;
    margin-right: 6px;
}

.as-info-grid {
    line-height: 1.6;
}

.as-info-cell {
    display: flex;
    align-items: baseline;
    padding: 6px 0;
    font-size: 13px;
}

.as-info-label {
    flex: 0 0 72px;
    color: #909399;
}

.as-info-value {
    flex: 1;
    color: #303133;
    word-break: break-all;
}

.as-form {
    margin-top: 2px;
}

/* 统一表单控件之间的纵向间距，避免卡片内过于松散 */
.as-form ::v-deep .el-form-item {
    margin-bottom: 18px;
}

.as-form ::v-deep .el-form-item:last-child {
    margin-bottom: 0;
}

/* 上传组件自带提示与自定义说明重复，隐藏自带提示 */
.as-form ::v-deep .el-upload__tip {
    display: none;
}

/* 上传框与下方说明拉开间距，避免控件贴太紧 */
.as-form ::v-deep .el-upload {
    line-height: normal;
}

.as-tip {
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 10px;
}

.dialog-footer {
    text-align: right;
}
</style>

<style>
.after-sale-dialog {
    border-radius: 10px;
    overflow: hidden;
}

.after-sale-dialog .el-dialog__header {
    padding: 16px 20px;
    background: #fff;
    border-bottom: 1px solid #ebeef5;
}

.after-sale-dialog .el-dialog__body {
    padding: 18px 20px;
    background: #fafbfc;
}

.after-sale-dialog .el-dialog__footer {
    padding: 12px 20px 16px;
    background: #fff;
    border-top: 1px solid #ebeef5;
}
</style>
